import type { PaginationModel } from '@montugroup/design-system';
import { PaginationVariant, Table } from '@montugroup/design-system';
import { useDebouncedCallback } from '@montugroup/frontend-common';
import { Box, Stack, Typography } from '@mui/material';
import type { Column, Row } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import TableSearchInput from '@/components/common/TableSearchInput';
import { INVENTORY_STATUS } from '@/constants/inventory';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import type { PharmacyInventoryHistory } from '@montugroup/circuit-shared/types/pharmacy';

import useInventoryHistory from '@/hooks/useGetInventoryHistory';
import { formatISODateHelper } from '@/utils/helpers';
import { dateColumnSort } from '@/utils/table';

function AdjustmentOverview() {
  const [search, setSearch] = useState('');
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({ page: 0, pageSize: 25 });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder();

  const { data, isLoading } = useInventoryHistory({ page, pageSize }, sortingOrder, search);

  const renderSimpleValueTableCell = ({
    row,
    column,
  }: {
    row: Row<PharmacyInventoryHistory>;
    column: Column<PharmacyInventoryHistory>;
  }) => <Box>{row.getValue(column.id)}</Box>;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'created_date',
        header: 'Date',
        enableSorting: true,
        accessorFn: (originalRow: { createdDate: string }) => formatISODateHelper(originalRow.createdDate),
        sortingFn: (rowA: { original: { createdDate: string } }, rowB: { original: { createdDate: string } }) =>
          dateColumnSort(rowA.original.createdDate, rowB.original.createdDate),
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'productName',
        header: 'Product',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'adjustedBy',
        header: 'Adjusted By',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'adjustedType',
        header: 'Adjusted Type',
        accessorFn: (originalRow: { adjustmentType: string | number }) =>
          Number(originalRow.adjustmentType) === Number(INVENTORY_STATUS.INCREMENT) ? 'Increase' : 'Decrease',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity Change',
        accessorFn: (originalRow: { adjustmentType: string | number; quantity: string }) =>
          Number(originalRow.adjustmentType) === Number(INVENTORY_STATUS.INCREMENT)
            ? originalRow.quantity
            : `-${originalRow.quantity}`,
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'reference',
        header: 'Reference',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
    ],
    [renderSimpleValueTableCell, dateColumnSort],
  );

  const handleSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, 250);

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => {
      setPaginationModel(model);
      setShouldResetPageIndex(false);
    },
    [setPaginationModel, setShouldResetPageIndex],
  );

  useEffect(() => {
    setShouldResetPageIndex(true);
  }, [search, setShouldResetPageIndex]);

  return (
    <Box>
      <Typography variant="h5" component="h2" paddingTop={5} paddingBottom={6}>
        Adjustment Overview
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={(theme) => theme.spacing(4)}
        justifyContent="space-between"
        paddingBottom={6}
      >
        <TableSearchInput value={search} onChange={handleSearch} />
      </Stack>
      <Table
        data={data?.inventoryHistory || []}
        columns={columns}
        showPagination
        manualPagination
        shouldResetPageIndex={shouldResetPageIndex}
        pageSize={pageSize}
        total={data?.count || 0}
        paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
        onPaginationModelChange={handlePaginationModelChange}
        onSortingChange={handleSortingOrderChange}
        isLoading={isLoading}
      />
    </Box>
  );
}

export default AdjustmentOverview;
